import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import { useInfiniteQuery } from "@tanstack/react-query"
import { LOGS_BY_PROJECT_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { useEffect, useMemo } from "react"
import { AppNotification, DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { parseCookies } from "nookies"
import PmsLoader from "@components/common/loader"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { useAuth } from "@stores/auth-store"
import ActivityCard from "./activity-card"
import { ScrollArea } from "@components/common/scroll-area"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@components/common/button"

interface Props {
  project: DictType
}
export function ProjectActivity({ project }: Props) {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryKey = searchParam.get("revalidate")
  const isHebrew = locale === "he"

  const getProjectLogs = async ({ paging_per_page, paging_start_page }: DictType) => {
    const response = await axios.get(
      SERVER_URL +
        LOGS_BY_PROJECT_ID_ENDPOINT +
        `/${project?.id}?get_subitems=true&paging_per_page=${paging_per_page}&paging_start_page=${paging_start_page}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const orgId = authConfig?.last_organization_id
  const {
    data: activities,
    refetch: refetchLogs,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ["activity-notifications"],
    queryFn: ({ pageParam = 1 }) =>
      getProjectLogs({
        paging_per_page: 30,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  const activitiesData = useMemo(() => {
    const temp: DictType = {}

    activities &&
      activities?.pages?.map((page: any) =>
        page?.items?.map((notification: AppNotification, index: number) => {
          const notificationDate = formatter.format(notification?.created_at)
          if (temp[notificationDate]) {
            return temp[notificationDate].push(notification)
          } else {
            return (temp[notificationDate] = [notification])
          }
        }),
      )

    return temp
  }, [isFetched, activities?.pages?.length])

  useEffect(() => {
    // Revalidate project when tasks and project is updated
    if (queryKey === "projects" || queryKey === "tasks") {
      refetchLogs()
      navigate(`${location.pathname}`)
    }
  }, [queryKey])

  useEffect(() => {
    refetchLogs()
  }, [])

  return (
    <Card className='max-h-[95vh] border-none shadow-none'>
      <CardHeader className='flex flex-row items-center justify-between gap-2 space-y-0 border-b py-5'>
        <div className={cn("grid flex-1 gap-1 text-center", isHebrew ? "text-right" : "text-left")}>
          <CardTitle>
            {translate(project?.title)} &nbsp;
            {translate("Activity")}
          </CardTitle>
        </div>
      </CardHeader>

      {isLoading || Object.keys(activitiesData).length < 1 ? (
        <PmsLoader />
      ) : (
        <CardContent className='gap-6 px-2 pt-4 sm:px-6 sm:pt-6'>
          <ScrollArea className='h-[80vh]'>
            {Object.keys(activitiesData)?.map((day: any, index) => (
              <div key={index} className='mb-6'>
                <h2
                  className={cn(
                    "text-xl font-semibold text-gray-700",
                    index % 2 === 1 ? "text-center" : "text-center",
                  )}
                >
                  {format(new Date(day), "MMM d,yyy", {
                    locale: isHebrew ? he : undefined,
                  })}
                </h2>
                <div className='mt-4'>
                  {activitiesData[day]?.map((activity: DictType, idx: number) => (
                    <ActivityCard key={idx} activity={activity} reverse={index % 2 === 1} />
                  ))}
                </div>
              </div>
            ))}

            {Object.keys(activitiesData).length < 1 && (
              <p className='mb-4 text-lg font-medium text-muted-foreground'>
                {translate("Sorry we didn't find anything")}.
              </p>
            )}
            <div className='flex w-full items-center justify-center'>
              {isLoading && (
                <div style={{ width: "100%" }}>
                  <PmsLoader />
                </div>
              )}
              {hasNextPage ? (
                <Button variant={"link"} className='text-primary' onClick={() => fetchNextPage()}>
                  {translate("Load More")}
                </Button>
              ) : null}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  )
}
